import { httpService } from "./httpService";

/**
 * Broker catalog management (admin).
 * Backend: fleetmd-backend BrokerController + Phase A.1 / V113 schema.
 */

export const getBrokers = async () =>
  httpService({ url: "/brokers", options: { method: "GET" } });

export const getBroker = async (id) =>
  httpService({ url: `/brokers/${id}`, options: { method: "GET" } });

/**
 * @param {{
 *   code: string, name: string,
 *   parentCompany?: string, contactPhone?: string, contactEmail?: string,
 *   providerPortalUrl?: string, notes?: string, active?: boolean,
 *   requiredTrainingModuleIds?: string[],
 *   requiredPolicyDocumentIds?: string[],
 * }} payload
 */
export const createBroker = async (payload) =>
  httpService({ url: "/brokers", options: { method: "POST", data: payload } });

export const updateBroker = async (id, payload) =>
  httpService({ url: `/brokers/${id}`, options: { method: "PUT", data: payload } });

export const deactivateBroker = async (id) =>
  httpService({ url: `/brokers/${id}`, options: { method: "DELETE" } });

/**
 * Per-broker compliance report — all drivers servicing this broker, their
 * required-training + required-policy completion status, missing items.
 */
export const getBrokerCompliance = async (id) =>
  httpService({ url: `/brokers/${id}/compliance`, options: { method: "GET" } });

/**
 * Trigger a browser download of the per-broker audit packet ZIP. Streams
 * binary so it bypasses httpService (which assumes JSON). Reads the same
 * AUTH_TOKEN that httpService uses.
 */
export const downloadBrokerAuditPacket = async (brokerId, brokerCode) => {
  const token = window.sessionStorage.getItem("AUTH_TOKEN");
  const apiUrl = process.env.REACT_APP_API_URL;
  const res = await fetch(`${apiUrl}/brokers/${brokerId}/audit-packet`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Audit packet download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `audit-${(brokerCode || brokerId).replace(/[^A-Za-z0-9._-]/g, "_")}-${stamp}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/** Recent audit-packet download history for one broker (max 50, newest first). */
export const getBrokerAuditPacketHistory = async (brokerId) =>
  httpService({ url: `/brokers/${brokerId}/audit-packet/history`, options: { method: "GET" } });

/** Bulk-email every non-compliant driver for a broker with what they're missing. */
export const nudgeBrokerNonCompliant = async (brokerId) =>
  httpService({ url: `/brokers/${brokerId}/nudge-noncompliant`, options: { method: "POST" } });

/** Per-broker schedule for auto-emailing the audit packet. */
export const getBrokerAuditSchedule = async (brokerId) =>
  httpService({ url: `/brokers/${brokerId}/audit-schedule`, options: { method: "GET" } });

export const saveBrokerAuditSchedule = async (brokerId, payload) =>
  httpService({ url: `/brokers/${brokerId}/audit-schedule`, options: { method: "PUT", data: payload } });

/** Tenant-wide audit-readiness rollup. */
export const getComplianceOverview = async () =>
  httpService({ url: `/compliance/overview`, options: { method: "GET" } });

/** Per-broker webhook config (enabled flag + masked secret indicator). */
export const getBrokerWebhookConfig = async (brokerId) =>
  httpService({ url: `/brokers/${brokerId}/webhook-config`, options: { method: "GET" } });

/** Rotate / set the webhook config. Returns plaintext secret only when rotate=true. */
export const saveBrokerWebhookConfig = async (brokerId, { enabled, rotate }) =>
  httpService({
    url: `/brokers/${brokerId}/webhook-config?enabled=${enabled ? "true" : "false"}&rotate=${rotate ? "true" : "false"}`,
    options: { method: "POST" },
  });

/** Recent inbound webhook events for this broker (max 200, newest first). */
export const getBrokerWebhookEvents = async (brokerId) =>
  httpService({ url: `/brokers/${brokerId}/webhook-events`, options: { method: "GET" } });

/** Re-run processing on one webhook event (admin retry). */
export const retryBrokerWebhookEvent = async (brokerId, eventId) =>
  httpService({
    url: `/brokers/${brokerId}/webhook-events/${eventId}/retry`,
    options: { method: "POST" },
  });

/**
 * Dashboard widget — compliance percentage per broker (lowest-first).
 */
export const getBrokerComplianceSummary = async () =>
  httpService({ url: "/brokers/compliance-summary", options: { method: "GET" } });

/** Re-uses /api/v1/training/modules from existing onboarding catalog. */
export const getTrainingCatalog = async () =>
  httpService({ url: "/training/modules", options: { method: "GET" } });

export const getPolicyCatalog = async () =>
  httpService({ url: "/policies", options: { method: "GET" } });

// --------- Broker contracts (company ↔ broker bindings) ---------

export const getBrokerContracts = async ({ companyId, brokerId } = {}) => {
  const q = [];
  if (companyId) q.push(`companyId=${companyId}`);
  if (brokerId) q.push(`brokerId=${brokerId}`);
  return httpService({
    url: `/broker-contracts${q.length ? "?" + q.join("&") : ""}`,
    options: { method: "GET" },
  });
};

/** Search customer companies for the contract picker (reuses existing client API). */
export const searchCustomerCompanies = async (searchTerm = "") =>
  httpService({
    url: "/customers/companies/search",
    options: {
      method: "POST",
      data: { searchTerm, paging: { page: 0, size: 100 } },
    },
  });

export const createBrokerContract = async (payload) =>
  httpService({
    url: "/broker-contracts",
    options: { method: "POST", data: payload },
  });

export const updateBrokerContract = async (id, payload) =>
  httpService({
    url: `/broker-contracts/${id}`,
    options: { method: "PUT", data: payload },
  });

export const deactivateBrokerContract = async (id) =>
  httpService({
    url: `/broker-contracts/${id}`,
    options: { method: "DELETE" },
  });
